import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, Plus, Trash2, Save, CheckCircle2 } from "lucide-react";

const SECTIONS = [
    { key: "identification", label: "1. Identification" },
    { key: "contexte", label: "2. Contexte & justification" },
    { key: "objectifs", label: "3. Objectifs" },
    { key: "public", label: "4. Public visé" },
    { key: "prerequis", label: "5. Prérequis" },
    { key: "duree", label: "6. Durée & organisation" },
    { key: "programme", label: "7. Programme détaillé" },
    { key: "methodes", label: "8. Méthodes pédagogiques" },
    { key: "moyens", label: "9. Moyens pédagogiques" },
    { key: "dpc", label: "10. Références & DPC" },
];

const METHODES_OPTIONS = [
    "Exposés théoriques",
    "Ateliers pratiques",
    "Études de cas",
    "Simulations / jeux de rôle",
];

const MODALITE_OPTIONS = ["Présentiel", "Distanciel", "Mixte"];

const DEFAULT_PROGRAMME = {
    intitule_formation: "",
    sous_titre: "",
    contexte_professionnel: "",
    probleme_rencontre: "",
    interet_profession: "",
    objectif_general: "",
    competences: [""],
    professions_concernees: "",
    niveau_experience: "",
    diplomes_requis: "",
    connaissances_necessaires: "",
    duree_heures: 7,
    nombre_jours: 1,
    modalite: "Présentiel",
    modules: [{ titre: "", objectifs: "", contenu: "", duree: "" }],
    methodes: [],
    methode_autre: "",
    supports_remis: "",
    materiel_utilise: "",
    outils_numeriques: "",
    evaluation_initiale: "",
    evaluation_formative: "",
    evaluation_finale: "",
    feuilles_emargement: "",
    attestation_formation: "",
    suivi_post_formation: "",
    adaptations_possibles: "",
    referent_handicap: "",
    sources_scientifiques: "",
    recommandations_professionnelles: "",
    analyse_initiale_pratiques: "",
    apports_theoriques: "",
    mise_en_oeuvre: "",
};

function Field({ label, required, children, hint }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-brand-ink">
                {label} {required && <span className="text-brand-orange">*</span>}
            </label>
            {children}
            {hint && <div className="mt-1.5 text-xs text-brand-smoke">{hint}</div>}
        </div>
    );
}

function TextInput({ value, onChange, testId, ...rest }) {
    return (
        <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-base outline-none transition-all focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
            {...rest}
        />
    );
}

function TextArea({ value, onChange, rows = 4, testId, ...rest }) {
    return (
        <textarea
            rows={rows}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-base outline-none transition-all focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
            {...rest}
        />
    );
}

function computeCompletion(d) {
    const fields = [
        d.intitule_formation,
        d.contexte_professionnel,
        d.probleme_rencontre,
        d.objectif_general,
        (d.competences || []).filter((c) => c?.trim()).length > 0 ? "ok" : "",
        d.professions_concernees,
        d.diplomes_requis,
        d.duree_heures ? "ok" : "",
        (d.modules || []).filter((m) => m?.titre?.trim()).length > 0 ? "ok" : "",
        (d.methodes || []).length > 0 ? "ok" : "",
        d.supports_remis,
        d.sources_scientifiques,
    ];
    const filled = fields.filter((v) => (typeof v === "string" ? v.trim() : !!v)).length;
    return Math.round((filled / fields.length) * 100);
}

export default function ProgrammeForm({ mode = "edit", onSubmitted }) {
    const [data, setData] = useState(DEFAULT_PROGRAMME);
    const [activeSection, setActiveSection] = useState(SECTIONS[0].key);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const dirtyRef = useRef(false);
    const dataRef = useRef(data);
    dataRef.current = data;

    useEffect(() => {
        api.get("/formateur/programme")
            .then((r) => {
                if (r.data?.data) {
                    setData({ ...DEFAULT_PROGRAMME, ...r.data.data });
                }
            })
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);

    // Auto-save every 10s if dirty
    const saveDraft = useCallback(async () => {
        if (!dirtyRef.current) return;
        setSaving(true);
        try {
            await api.put("/formateur/programme", dataRef.current);
            dirtyRef.current = false;
            setLastSavedAt(new Date());
        } catch {
            // silent
        } finally {
            setSaving(false);
        }
    }, []);

    useEffect(() => {
        const t = setInterval(saveDraft, 10000);
        return () => clearInterval(t);
    }, [saveDraft]);

    const update = (patch) => {
        setData((d) => ({ ...d, ...patch }));
        dirtyRef.current = true;
    };

    const updateCompetence = (idx, val) => {
        const next = [...(data.competences || [])];
        next[idx] = val;
        update({ competences: next });
    };
    const addCompetence = () => update({ competences: [...(data.competences || []), ""] });
    const removeCompetence = (idx) =>
        update({ competences: (data.competences || []).filter((_, i) => i !== idx) });

    const updateModule = (idx, patch) => {
        const next = [...(data.modules || [])];
        next[idx] = { ...next[idx], ...patch };
        update({ modules: next });
    };
    const addModule = () =>
        update({ modules: [...(data.modules || []), { titre: "", objectifs: "", contenu: "", duree: "" }] });
    const removeModule = (idx) =>
        update({ modules: (data.modules || []).filter((_, i) => i !== idx) });

    const toggleMethode = (m) => {
        const cur = new Set(data.methodes || []);
        if (cur.has(m)) cur.delete(m);
        else cur.add(m);
        update({ methodes: Array.from(cur) });
    };

    const saveNow = async () => {
        await saveDraft();
        toast.success("Brouillon enregistré");
    };

    const submit = async () => {
        if (!data.intitule_formation?.trim()) {
            toast.error("L'intitulé de la formation est obligatoire.");
            return;
        }
        if (!(data.modules || []).some((m) => m.titre?.trim())) {
            toast.error("Au moins un module avec un titre est obligatoire.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/formateur/programme/submit", data);
            dirtyRef.current = false;
            toast.success("Programme validé");
            onSubmitted?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const completion = computeCompletion(data);
    const activeIndex = SECTIONS.findIndex((s) => s.key === activeSection);
    const next = SECTIONS[activeIndex + 1];
    const prev = SECTIONS[activeIndex - 1];

    if (loading) {
        return <div className="py-20 text-center text-brand-smoke">Chargement du programme…</div>;
    }

    return (
        <div className="grid gap-8 lg:grid-cols-12" data-testid="programme-form">
            {/* Section nav */}
            <aside className="lg:col-span-4 xl:col-span-3">
                <div className="sticky top-6 rounded-2xl border border-brand-line bg-white p-5 shadow-premium">
                    <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                            Progression
                        </div>
                        <div className="font-display text-sm text-brand-orange">{completion}%</div>
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-brand-line">
                        <div
                            className="h-full bg-brand-orange transition-all"
                            style={{ width: `${completion}%` }}
                        />
                    </div>
                    <ol className="mt-6 space-y-1">
                        {SECTIONS.map((s) => (
                            <li key={s.key}>
                                <button
                                    type="button"
                                    onClick={() => setActiveSection(s.key)}
                                    data-testid={`programme-section-${s.key}`}
                                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                                        activeSection === s.key
                                            ? "bg-brand-ink text-white"
                                            : "text-brand-ink/75 hover:bg-brand-line/40"
                                    }`}
                                >
                                    <span>{s.label}</span>
                                    <ChevronRight className="h-3.5 w-3.5" />
                                </button>
                            </li>
                        ))}
                    </ol>
                </div>
            </aside>

            <div className="lg:col-span-8 xl:col-span-9">
                <div className="rounded-2xl border border-brand-line bg-white p-8 shadow-premium md:p-10">
                    {/* IDENTIFICATION */}
                    {activeSection === "identification" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">1. Identification</h2>
                            <Field label="Intitulé de la formation" required>
                                <TextInput value={data.intitule_formation} onChange={(v) => update({ intitule_formation: v })} testId="field-intitule" />
                            </Field>
                            <Field label="Sous-titre">
                                <TextInput value={data.sous_titre} onChange={(v) => update({ sous_titre: v })} testId="field-sous-titre" />
                            </Field>
                        </div>
                    )}

                    {activeSection === "contexte" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">2. Contexte & justification</h2>
                            <Field label="Contexte professionnel" required>
                                <TextArea value={data.contexte_professionnel} onChange={(v) => update({ contexte_professionnel: v })} testId="field-contexte" />
                            </Field>
                            <Field label="Problème rencontré" required>
                                <TextArea value={data.probleme_rencontre} onChange={(v) => update({ probleme_rencontre: v })} testId="field-probleme" />
                            </Field>
                            <Field label="Intérêt pour la profession">
                                <TextArea value={data.interet_profession} onChange={(v) => update({ interet_profession: v })} testId="field-interet" />
                            </Field>
                        </div>
                    )}

                    {activeSection === "objectifs" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">3. Objectifs</h2>
                            <Field label="Objectif général" required>
                                <TextArea value={data.objectif_general} onChange={(v) => update({ objectif_general: v })} testId="field-objectif-general" />
                            </Field>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-brand-ink">
                                    Compétences visées <span className="text-brand-orange">*</span>
                                </label>
                                <div className="mt-2 space-y-2">
                                    {(data.competences || []).map((c, i) => (
                                        <div key={i} className="flex gap-2">
                                            <TextInput
                                                value={c}
                                                onChange={(v) => updateCompetence(i, v)}
                                                testId={`field-competence-${i}`}
                                                placeholder="Compétence visée"
                                            />
                                            {(data.competences || []).length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeCompetence(i)}
                                                    data-testid={`remove-competence-${i}`}
                                                    className="mt-2 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-brand-line text-brand-smoke hover:border-red-300 hover:text-red-600"
                                                    aria-label="Supprimer"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={addCompetence}
                                    data-testid="add-competence"
                                    className="mt-3 inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 text-xs font-semibold hover:border-brand-orange hover:text-brand-orange"
                                >
                                    <Plus className="h-3.5 w-3.5" /> Ajouter une compétence
                                </button>
                            </div>
                        </div>
                    )}

                    {activeSection === "public" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">4. Public visé</h2>
                            <Field label="Professions concernées" required>
                                <TextArea value={data.professions_concernees} onChange={(v) => update({ professions_concernees: v })} testId="field-professions" />
                            </Field>
                            <Field label="Niveau d'expérience">
                                <TextArea value={data.niveau_experience} onChange={(v) => update({ niveau_experience: v })} testId="field-niveau" />
                            </Field>
                        </div>
                    )}

                    {activeSection === "prerequis" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">5. Prérequis</h2>
                            <Field label="Diplômes requis" required>
                                <TextArea value={data.diplomes_requis} onChange={(v) => update({ diplomes_requis: v })} testId="field-diplomes" />
                            </Field>
                            <Field label="Connaissances nécessaires">
                                <TextArea value={data.connaissances_necessaires} onChange={(v) => update({ connaissances_necessaires: v })} testId="field-connaissances" />
                            </Field>
                        </div>
                    )}

                    {activeSection === "duree" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">6. Durée & organisation</h2>
                            <div className="grid grid-cols-3 gap-4">
                                <Field label="Durée (heures)" required>
                                    <input
                                        type="number"
                                        min={1}
                                        max={200}
                                        value={data.duree_heures ?? 7}
                                        onChange={(e) => update({ duree_heures: parseInt(e.target.value || "0", 10) })}
                                        data-testid="field-duree-heures"
                                        className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                    />
                                </Field>
                                <Field label="Jours" required>
                                    <input
                                        type="number"
                                        min={1}
                                        max={30}
                                        value={data.nombre_jours ?? 1}
                                        onChange={(e) => update({ nombre_jours: parseInt(e.target.value || "0", 10) })}
                                        data-testid="field-nombre-jours"
                                        className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                    />
                                </Field>
                                <Field label="Modalité" required>
                                    <select
                                        value={data.modalite || "Présentiel"}
                                        onChange={(e) => update({ modalite: e.target.value })}
                                        data-testid="field-modalite"
                                        className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 outline-none focus:border-brand-orange"
                                    >
                                        {MODALITE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </Field>
                            </div>
                        </div>
                    )}

                    {activeSection === "programme" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">7. Programme détaillé</h2>
                            <p className="text-sm text-brand-smoke">
                                Décrivez chaque module de votre formation. Au moins un module avec un titre est obligatoire.
                            </p>
                            <div className="space-y-5">
                                {(data.modules || []).map((m, i) => (
                                    <div key={i} className="rounded-2xl border border-brand-line bg-[#FAFAFA] p-5" data-testid={`module-card-${i}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="font-display text-lg">Module {i + 1}</div>
                                            {(data.modules || []).length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeModule(i)}
                                                    data-testid={`remove-module-${i}`}
                                                    className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" /> Supprimer
                                                </button>
                                            )}
                                        </div>
                                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                                            <Field label="Titre">
                                                <TextInput value={m.titre} onChange={(v) => updateModule(i, { titre: v })} testId={`module-${i}-titre`} />
                                            </Field>
                                            <Field label="Durée">
                                                <TextInput value={m.duree} onChange={(v) => updateModule(i, { duree: v })} testId={`module-${i}-duree`} placeholder="ex: 1h30" />
                                            </Field>
                                        </div>
                                        <Field label="Objectifs">
                                            <TextArea rows={2} value={m.objectifs} onChange={(v) => updateModule(i, { objectifs: v })} testId={`module-${i}-objectifs`} />
                                        </Field>
                                        <div className="mt-4">
                                            <Field label="Contenu">
                                                <TextArea value={m.contenu} onChange={(v) => updateModule(i, { contenu: v })} testId={`module-${i}-contenu`} />
                                            </Field>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={addModule}
                                data-testid="add-module"
                                className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 text-xs font-semibold hover:border-brand-orange hover:text-brand-orange"
                            >
                                <Plus className="h-3.5 w-3.5" /> Ajouter un module
                            </button>
                        </div>
                    )}

                    {activeSection === "methodes" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">8. Méthodes pédagogiques</h2>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {METHODES_OPTIONS.map((m) => {
                                    const checked = (data.methodes || []).includes(m);
                                    return (
                                        <label
                                            key={m}
                                            className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors ${
                                                checked ? "border-brand-orange bg-brand-orange/5" : "border-brand-line hover:border-brand-orange/50"
                                            }`}
                                            data-testid={`methode-${m.replace(/\s/g, "-").toLowerCase()}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleMethode(m)}
                                                className="h-4 w-4 accent-brand-orange"
                                            />
                                            <span className="text-sm">{m}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            <Field label="Autre méthode">
                                <TextArea value={data.methode_autre} onChange={(v) => update({ methode_autre: v })} testId="field-methode-autre" rows={2} />
                            </Field>
                        </div>
                    )}

                    {activeSection === "moyens" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">9. Moyens pédagogiques & techniques</h2>
                            <Field label="Supports remis aux stagiaires">
                                <TextArea value={data.supports_remis} onChange={(v) => update({ supports_remis: v })} testId="field-supports" />
                            </Field>
                            <Field label="Matériel utilisé">
                                <TextArea value={data.materiel_utilise} onChange={(v) => update({ materiel_utilise: v })} testId="field-materiel" />
                            </Field>
                            <Field label="Outils numériques">
                                <TextArea value={data.outils_numeriques} onChange={(v) => update({ outils_numeriques: v })} testId="field-outils" />
                            </Field>
                        </div>
                    )}

                    {activeSection === "evaluation" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">10. Modalités d&apos;évaluation</h2>
                            <Field label="Évaluation initiale">
                                <TextArea value={data.evaluation_initiale} onChange={(v) => update({ evaluation_initiale: v })} testId="field-eval-initiale" />
                            </Field>
                            <Field label="Évaluation formative">
                                <TextArea value={data.evaluation_formative} onChange={(v) => update({ evaluation_formative: v })} testId="field-eval-formative" />
                            </Field>
                            <Field label="Évaluation finale">
                                <TextArea value={data.evaluation_finale} onChange={(v) => update({ evaluation_finale: v })} testId="field-eval-finale" />
                            </Field>
                        </div>
                    )}

                    {activeSection === "suivi" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">11. Modalités de suivi</h2>
                            <Field label="Feuilles d'émargement">
                                <TextArea value={data.feuilles_emargement} onChange={(v) => update({ feuilles_emargement: v })} testId="field-emargement" />
                            </Field>
                            <Field label="Attestation de formation">
                                <TextArea value={data.attestation_formation} onChange={(v) => update({ attestation_formation: v })} testId="field-attestation" />
                            </Field>
                            <Field label="Suivi post-formation">
                                <TextArea value={data.suivi_post_formation} onChange={(v) => update({ suivi_post_formation: v })} testId="field-suivi" />
                            </Field>
                        </div>
                    )}

                    {activeSection === "accessibilite" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">12. Accessibilité handicap</h2>
                            <Field label="Adaptations possibles">
                                <TextArea value={data.adaptations_possibles} onChange={(v) => update({ adaptations_possibles: v })} testId="field-adaptations" />
                            </Field>
                            <Field label="Référent handicap (nom + contact)">
                                <TextInput value={data.referent_handicap} onChange={(v) => update({ referent_handicap: v })} testId="field-referent" />
                            </Field>
                        </div>
                    )}

                    {activeSection === "dpc" && (
                        <div className="space-y-5">
                            <h2 className="font-display text-3xl">13. Références & DPC</h2>
                            <Field label="Sources scientifiques">
                                <TextArea value={data.sources_scientifiques} onChange={(v) => update({ sources_scientifiques: v })} testId="field-sources" />
                            </Field>
                            <Field label="Recommandations professionnelles">
                                <TextArea value={data.recommandations_professionnelles} onChange={(v) => update({ recommandations_professionnelles: v })} testId="field-recommandations" />
                            </Field>
                            <Field label="Analyse initiale des pratiques">
                                <TextArea value={data.analyse_initiale_pratiques} onChange={(v) => update({ analyse_initiale_pratiques: v })} testId="field-analyse" />
                            </Field>
                            <Field label="Apports théoriques">
                                <TextArea value={data.apports_theoriques} onChange={(v) => update({ apports_theoriques: v })} testId="field-apports" />
                            </Field>
                            <Field label="Mise en œuvre">
                                <TextArea value={data.mise_en_oeuvre} onChange={(v) => update({ mise_en_oeuvre: v })} testId="field-mise-en-oeuvre" />
                            </Field>
                        </div>
                    )}

                    {/* Footer navigation */}
                    <div className="mt-10 flex flex-col items-stretch justify-between gap-4 border-t border-brand-line pt-6 sm:flex-row sm:items-center">
                        <div className="flex items-center gap-3 text-xs text-brand-smoke" data-testid="programme-save-status">
                            {saving ? (
                                <span className="inline-flex items-center gap-2"><Save className="h-3.5 w-3.5 animate-pulse" /> Sauvegarde…</span>
                            ) : lastSavedAt ? (
                                <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Brouillon sauvegardé à {lastSavedAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                            ) : (
                                <span>Auto-save toutes les 10 s</span>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-3">
                            {prev && (
                                <button
                                    type="button"
                                    onClick={() => setActiveSection(prev.key)}
                                    className="rounded-full border border-brand-line px-5 py-2.5 text-sm font-semibold hover:border-brand-orange hover:text-brand-orange"
                                >
                                    ← Précédent
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={saveNow}
                                data-testid="programme-save-draft"
                                className="inline-flex items-center gap-2 rounded-full border border-brand-line px-5 py-2.5 text-sm font-semibold hover:border-brand-orange hover:text-brand-orange"
                            >
                                <Save className="h-4 w-4" /> Enregistrer
                            </button>
                            {next ? (
                                <button
                                    type="button"
                                    onClick={() => setActiveSection(next.key)}
                                    data-testid="programme-next"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-coal"
                                >
                                    Suivant <ChevronRight className="h-4 w-4" />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={submit}
                                    disabled={submitting}
                                    data-testid="programme-submit"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                                >
                                    {submitting ? "Validation…" : (mode === "onboarding" ? "Valider mon programme" : "Enregistrer définitivement")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
